import { writable, derived, get } from 'svelte/store'
import { locale } from 'svelte-i18n'
import oAuthConfig from './oauth_config.js'

// Load the token from the browsers local storage
let storedToken = null
const storedTokenStr = localStorage.getItem('token')
if (
  storedTokenStr &&
  typeof storedTokenStr == 'string' &&
  storedTokenStr != 'undefined'
) {
  // The token in the localStorage looks well formed, lets use it
  console.log('Loading stored token from localStorage', storedTokenStr)
  storedToken = JSON.parse(storedTokenStr)
}

// A svelte store that stores the current token. Initialized with the value
// from the localStorage if available. Will be set from the oAuth flow.
const token = writable(storedToken)
token.subscribe((value) => {
  console.log('Token Subscriber', value)
  if (value) {
    localStorage.setItem('token', JSON.stringify(value))
  }
})

// Clears the token by clearing it in the svelte store and removing it from the
// localStorage
export function clearToken() {
  console.log('Clearing Token')
  token.update(() => null)
  localStorage.removeItem('token')
}

// Sets the current token. Used after a login flow
export function setToken(tokenValue) {
  console.log('Setting token store to', tokenValue)
  token.update(() => tokenValue)
}

// Returns whether there is currently a valid token
export function hasToken() {
  const tokenValue = get(token)
  return !!tokenValue
}

// This is a derived store that contains the information about the currently
// logged in user or an empty object ({}) if there is no logged in user OR the
// token is invalid.
export const currentUser = derived(
  token,
  async ($token, set) => {
    // The handling here is not ideal, the token should be refreshed if this
    // request fails.  but importing the fetchO function, which would
    // handle all of this, will create a cyclic dependency graph.
    if ($token) {
      const response = await fetchO(oAuthConfig.host + '/users/current_user/', {
        headers: {
          Authorization: $token.token_type + ' ' + $token.access_token,
        },
      })
      if (response.status == 200) {
        const userinfo = await response.json()
        set(userinfo)
      } else {
        // Can't fetch user info despite having a token, leave it to other calls to try to refresh
        set({})
      }
    } else {
      // No token, no user-info
      set({})
    }
  },
  {}
)

// Remember the first locale ever used (most likely based on the browser) to be
// able to reset the locale after a logout.
let initialLocale
locale.subscribe((value) => {
  if (!initialLocale) {
    initialLocale = value
  }
})

// When the current user-info changes, also update the locale used
currentUser.subscribe((userinfo) => {
  console.log('Adapting Language to', userinfo)
  if (userinfo && userinfo.language) {
    // There is a userinfo and it contains a language, use it as locale
    locale.set(userinfo.language)
  } else if (initialLocale) {
    // There is no userinfo or no language but an initially used locale
    // which is very likely from the browser, use this instead.
    locale.set(initialLocale)
  } else {
    // No information available, fallback to English
    locale.set('en')
  }
})

// Refreshes information about the current user (i.e. after changing user details)
export function refreshCurrentUser() {
  token.set(get(token))
}

async function refreshToken() {
  const formData = new FormData()
  formData.append('client_id', oAuthConfig.clientId)
  formData.append('grant_type', 'refresh_token')
  formData.append('refresh_token', get(token).refresh_token)

  const response = await fetch(oAuthConfig.host + '/o/token/', {
    method: 'POST',
    body: formData,
  })

  console.log('Refreshing token returned ' + response.status)
  if (response.status == 200) {
    const newToken = await response.json()
    console.log('Refreshed to new token', newToken)
    return newToken
  } else {
    return
  }
}

export async function fetchO(route, data) {
  const tokenValue = get(token)
  if (!tokenValue || tokenValue == {}) {
    console.log('No Token, can not fetch ' + route + ', redirecting to login')
    document.location.href = oAuthConfig.loginUrl
    return Promise.reject('No Token')
  }

  // If the provided route starts with http or https, us as it, otherwise
  // prepend the hostname
  let url
  if (route.startsWith('http://') || route.startsWith('https://')) {
    url = route
  } else {
    url = oAuthConfig.host + route
  }

  if (!data) {
    data = {}
  }

  if (!data['headers']) {
    data['headers'] = {}
  }

  data['headers']['Authorization'] =
    tokenValue.token_type + ' ' + tokenValue.access_token

  let response = await fetch(url, data)
  console.log('Request to ' + url + ' returned ' + response.status)
  if (response.status == 401) {
    // Once try to refresh the token
    const newToken = await refreshToken()
    if (newToken) {
      token.set(newToken)

      data['headers']['Authorization'] =
        newToken.token_type + ' ' + newToken.access_token
      response = await fetch(url, data)
    } else {
      // Refreshing the token did not work, invalidate the whole token and redirect to login
      clearToken()
      document.location.href = oAuthConfig.loginUrl
      return Promise.reject('Refresh Token Failed')
    }
  }

  return Promise.resolve(response)
}
export default fetchO

export async function patchO(route, json) {
  return await fetchO(route, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(json),
  })
}

export async function postO(route, json) {
  return await fetchO(route, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(json),
  })
}
